import type { StationSelectionMethod } from "../../model/aemet/aemet.ts";
import type { AemetProvenance } from "../../model/storage.ts";
import type { SubBasin } from "../../model/types.ts";

/**
 * Restricts `subBasins` to `onlyCodes` (order preserved) — `null`/undefined
 * keeps every sub-basin, the usual "whole project" dialog. Used to pre-scope
 * AemetBulkFetchDialog.tsx when it's opened from SeriesPanel.tsx's "gap left
 * over from an extend" notice, so "Extend all" only touches the sub-basins
 * actually affected instead of the whole project.
 */
export function scopeSubBasins(subBasins: SubBasin[], onlyCodes?: number[] | null): SubBasin[] {
  if (!onlyCodes) return subBasins;
  const codeSet = new Set(onlyCodes);
  return subBasins.filter((sb) => codeSet.has(sb.subBasinCode));
}

/**
 * The method already used for most of this project's AEMET-sourced
 * sub-basins, if any — so re-opening this dialog to complete/extend the
 * rest defaults to the SAME method instead of silently resetting to
 * "nearest" every time. Real bug reported live: reopening this dialog on
 * a MITECO+ROCIO import to fetch the remaining sub-basins defaulted back
 * to "nearest", risking a project with two different methods mixed across
 * sub-basins if the user didn't notice and re-pick "Malla ROCIO" by hand.
 * `method` is optional on `AemetProvenance` (old entries predate the
 * field) — those are skipped rather than counted as "nearest", so a
 * project with only old-format entries still falls back to the real
 * default ("nearest") below instead of a fabricated majority.
 */
export function mostCommonMethod(
  aemetProvenance: Record<number, AemetProvenance>
): StationSelectionMethod | null {
  const counts = new Map<StationSelectionMethod, number>();
  for (const provenance of Object.values(aemetProvenance)) {
    if (!provenance.method) continue;
    counts.set(provenance.method, (counts.get(provenance.method) ?? 0) + 1);
  }
  let best: StationSelectionMethod | null = null;
  let bestCount = 0;
  for (const [method, count] of counts) {
    if (count > bestCount) {
      best = method;
      bestCount = count;
    }
  }
  return best;
}
